import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard',              icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats',        icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads',        icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm',          icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents',       icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets',      icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot',       icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api',          icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding',     icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget',       icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics',    icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing',      icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings',     icon: '⚙️',  label: 'Settings' },
];

let customFieldCounter = 1000;

export default function ContactForm() {
  const { user } = useAuth();
  const [botId, setBotId]         = useState('');
  const [activeTab, setActiveTab] = useState('inline');

  const [title,      setTitle]      = useState('Contact Us');
  const [subtitle,   setSubtitle]   = useState("We'll get back to you as soon as possible.");
  const [btnText,    setBtnText]    = useState('Send Message →');
  const [color,      setColor]      = useState('#00d55b');
  const [width,      setWidth]      = useState('440');
  const [fontSize,   setFontSize]   = useState('14');
  const [rounded,    setRounded]    = useState('10');
  const [theme,      setTheme]      = useState('light');
  const [showPhone,  setShowPhone]  = useState(true);
  const [popupDelay, setPopupDelay] = useState(10);
  const [customFields, setCustomFields] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/business/profile').then(r => {
      setBotId(r.data.data?.bot_id || '');
    }).catch(() => {});

    axios.get('/api/business/contact-form-settings').then(r => {
      const d = r.data.data;
      if (!d) return;
      setTitle(d.title);
      setSubtitle(d.subtitle);
      setBtnText(d.btn_text);
      setColor(d.color);
      setWidth(d.width);
      setFontSize(d.font_size);
      setRounded(d.rounded);
      setTheme(d.theme);
      setShowPhone(!!d.show_phone);
      setPopupDelay(Number(d.popup_delay) || 10);
      if (d.custom_fields) {
        try { setCustomFields(JSON.parse(d.custom_fields)); } catch(e) {}
      }
    }).catch(() => {});
  }, []);

  const addCustomField = () => {
    if (customFields.length >= 5) {
      toast.error('Maximum 5 custom fields allowed');
      return;
    }
    setCustomFields(prev => [...prev, {
      id: `cf_${++customFieldCounter}`,
      label: '',
      type: 'text',       // always text
      required: false,
    }]);
  };

  const updateCustomField = (id, key, value) => {
    setCustomFields(prev => prev.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const deleteCustomField = (id) => {
    setCustomFields(prev => prev.filter(f => f.id !== id));
  };

  const handleSave = async () => {
    const emptyLabel = customFields.find(f => !f.label.trim());
    if (emptyLabel) { toast.error('Please fill in all custom field labels'); return; }
    const labels = customFields.map(f => f.label.trim().toLowerCase());
    if (new Set(labels).size !== labels.length) { toast.error('Custom field labels must be unique'); return; }

    setSaving(true);
    try {
      await axios.put('/api/business/contact-form-settings', {
        title, subtitle, btn_text: btnText, color, width,
        font_size: fontSize, rounded, theme,
        show_phone: showPhone, show_message: true, popup_delay: popupDelay,
        custom_fields: JSON.stringify(customFields),
      });
      toast.success('Contact form settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const standardFields = [
    { label: 'Full Name *',     type: 'text',     required: true },
    { label: 'Email Address *', type: 'email',    required: true },
    ...(showPhone ? [{ label: 'Phone Number', type: 'text', required: false }] : []),
    { label: 'Message',         type: 'textarea', required: false },
  ];
  const allPreviewFields = [...standardFields, ...customFields];

  const buildParams = (extra = {}) => {
    const enc = (v) => encodeURIComponent(String(v));
    const parts = [
      `id=${enc(botId)}`,
      `title=${enc(title)}`,
      `subtitle=${enc(subtitle)}`,
      `btntext=${enc(btnText)}`,
      `color=${enc(color)}`,
      `width=${enc(width + 'px')}`,
      `fontsize=${enc(fontSize)}`,
      `rounded=${enc(rounded)}`,
      `theme=${enc(theme)}`,
    ];
    if (!showPhone) parts.push('phone=false');
    if (customFields.length) parts.push(`custom_fields=${enc(JSON.stringify(customFields))}`);
    Object.entries(extra).forEach(([k, v]) => parts.push(`${k}=${enc(v)}`));
    return parts.join('&');
  };

  const inlineCode = `<!-- MSChat Contact Form -->\n<div id="mschat-contact-form"></div>\n<script src="https://mschat.msdiscoveries.com/form.js?${buildParams()}"></script>`;
  const popupCode  = `<!-- MSChat Popup Form -->\n<script src="https://mschat.msdiscoveries.com/form.js?${buildParams({ popup: 'true', delay: popupDelay })}"></script>`;

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success('Code copied!'); };

  const box  = { background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 20, marginBottom: 20 };
  const inp  = { width: '100%', padding: '9px 12px', background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13, boxSizing: 'border-box' };
  const lbl  = { fontSize: 12, color: '#888', display: 'block', marginBottom: 6 };
  const row  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 };
  const tog  = (active) => ({ padding: '7px 0', flex: 1, borderRadius: 8, border: `1px solid ${active ? color : '#1e1e1e'}`, background: active ? `${color}22` : '#0d0d0d', color: active ? color : '#555', fontSize: 13, cursor: 'pointer', fontWeight: active ? 700 : 400 });

  const isDark   = theme === 'dark';
  const prevBg   = isDark ? '#111' : '#fff';
  const prevText = isDark ? '#fff' : '#111';
  const prevSub  = '#888';
  const prevInp  = isDark ? '#1a1a1a' : '#fafafa';
  const prevBord = isDark ? '#2a2a2a' : '#e0e0e0';

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28, maxWidth: 1000 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>📝 Contact Form</h1>
        <p style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>Embed a customizable contact form on any website. Leads go directly to your MSChat dashboard.</p>

        {/* Tab Switch */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[['inline', '📌 Inline Embed'], ['popup', '🔔 Popup Form']].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              style={{ padding: '8px 20px', borderRadius: 8, border: `1px solid ${activeTab === key ? '#00d55b' : '#1e1e1e'}`, background: activeTab === key ? 'rgba(0,213,91,0.1)' : '#111', color: activeTab === key ? '#00d55b' : '#555', fontSize: 13, fontWeight: activeTab === key ? 600 : 400, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* ── LEFT ── */}
          <div>

            {/* Text */}
            <div style={box}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>✏️ Text</h3>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Form Title</label>
                <input style={inp} value={title} onChange={e => setTitle(e.target.value)} placeholder="Contact Us" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Subtitle</label>
                <input style={inp} value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="We'll get back to you..." />
              </div>
              <div>
                <label style={lbl}>Button Text</label>
                <input style={inp} value={btnText} onChange={e => setBtnText(e.target.value)} placeholder="Send Message →" />
              </div>
            </div>

            {/* Appearance */}
            <div style={box}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>🎨 Appearance</h3>
              <div style={row}>
                <div>
                  <label style={lbl}>Button Color</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="color" value={color} onChange={e => setColor(e.target.value)}
                      style={{ width: 40, height: 36, borderRadius: 8, border: '1px solid #1e1e1e', background: 'none', cursor: 'pointer', padding: 2 }} />
                    <input style={{ ...inp, flex: 1 }} value={color} onChange={e => setColor(e.target.value)} placeholder="#00d55b" />
                  </div>
                </div>
                <div>
                  <label style={lbl}>Theme</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setTheme('light')} style={tog(theme === 'light')}>☀️ Light</button>
                    <button onClick={() => setTheme('dark')}  style={tog(theme === 'dark')}>🌙 Dark</button>
                  </div>
                </div>
              </div>
              <div style={row}>
                <div>
                  <label style={lbl}>Form Width (px)</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {['340', '400', '440', '520', '600'].map(w => (
                      <button key={w} onClick={() => setWidth(w)}
                        style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${width === w ? color : '#1e1e1e'}`, background: width === w ? `${color}22` : '#0d0d0d', color: width === w ? color : '#555', fontSize: 12, cursor: 'pointer' }}>
                        {w}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={lbl}>Font Size (px)</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {['12', '13', '14', '15', '16'].map(f => (
                      <button key={f} onClick={() => setFontSize(f)}
                        style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${fontSize === f ? color : '#1e1e1e'}`, background: fontSize === f ? `${color}22` : '#0d0d0d', color: fontSize === f ? color : '#555', fontSize: 12, cursor: 'pointer' }}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label style={lbl}>Corner Roundness (px)</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[['0', 'Sharp'], ['6', 'Slight'], ['10', 'Round'], ['16', 'More'], ['24', 'Pill']].map(([r, name]) => (
                    <button key={r} onClick={() => setRounded(r)}
                      style={{ padding: '6px 14px', borderRadius: 7, border: `1px solid ${rounded === r ? color : '#1e1e1e'}`, background: rounded === r ? `${color}22` : '#0d0d0d', color: rounded === r ? color : '#555', fontSize: 12, cursor: 'pointer' }}>
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Standard Fields */}
            <div style={box}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>🗂️ Standard Fields</h3>
              <p style={{ fontSize: 12, color: '#555', marginBottom: 12 }}>Full Name, Email, and Message are always included.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setShowPhone(!showPhone)} style={tog(showPhone)}>📞 Phone {showPhone ? '✓' : '✕'}</button>
              </div>
            </div>

            {/* Custom Fields */}
            <div style={box}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>➕ Custom Fields</h3>
              <p style={{ fontSize: 12, color: '#555', marginBottom: 14 }}>Add extra fields below the standard ones. Max 5.</p>

              {customFields.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                  {customFields.map((field, idx) => (
                    <div key={field.id} style={{ background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 9, padding: '12px 12px 10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: '#555' }}>Field {idx + 1}</span>
                        <button
                          onClick={() => deleteCustomField(field.id)}
                          style={{ background: 'none', border: '1px solid #3a1a1a', borderRadius: 6, color: '#e05555', fontSize: 12, cursor: 'pointer', padding: '3px 9px' }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label style={lbl}>Field Label</label>
                        <input
                          style={inp}
                          value={field.label}
                          onChange={e => updateCustomField(field.id, 'label', e.target.value)}
                          placeholder="e.g. Company Name"
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => updateCustomField(field.id, 'required', !field.required)}
                          style={{
                            padding: '7px 14px', borderRadius: 8,
                            border: `1px solid ${field.required ? color : '#1e1e1e'}`,
                            background: field.required ? `${color}22` : '#111',
                            color: field.required ? color : '#555',
                            fontSize: 12, cursor: 'pointer',
                            fontWeight: field.required ? 700 : 400,
                          }}
                        >
                          {field.required ? '✓ Required' : '✕ Optional'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {customFields.length < 5 ? (
                <button
                  onClick={addCustomField}
                  style={{
                    width: '100%', padding: '10px', borderRadius: 9,
                    border: `1px dashed ${color}66`, background: `${color}0d`,
                    color: color, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  ➕ Add Custom Field {customFields.length > 0 ? `(${customFields.length}/5)` : ''}
                </button>
              ) : (
                <div style={{ fontSize: 12, color: '#555', textAlign: 'center', padding: '8px 0' }}>
                  Maximum 5 custom fields reached
                </div>
              )}
            </div>

            {/* Popup Delay */}
            {activeTab === 'popup' && (
              <div style={box}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>⏱️ Popup Delay</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[5, 10, 15, 20, 30].map(d => (
                    <button key={d} onClick={() => setPopupDelay(d)}
                      style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${popupDelay === d ? color : '#1e1e1e'}`, background: popupDelay === d ? `${color}22` : '#0d0d0d', color: popupDelay === d ? color : '#555', fontSize: 13, cursor: 'pointer' }}>
                      {d}s
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: '#555', marginTop: 8 }}>Form appears after {popupDelay} seconds on page load</div>
              </div>
            )}

            {/* Save */}
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={handleSave} disabled={saving}
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: saving ? '#1a1a1a' : color, color: saving ? '#555' : '#000', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
              >
                {saving ? '💾 Saving...' : '💾 Save Settings'}
              </button>
            </div>

            {/* Embed Code */}
            <div style={box}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
                {activeTab === 'inline' ? '📌 Inline Embed Code' : '🔔 Popup Embed Code'}
              </h3>
              <p style={{ fontSize: 12, color: '#555', marginBottom: 12 }}>
                {activeTab === 'inline'
                  ? 'Paste where you want the form to appear on your page.'
                  : <>Paste before <code style={{ color: '#00d55b' }}>&lt;/body&gt;</code> on your page.</>}
              </p>
              <div style={{ background: '#0d0d0d', borderRadius: 8, padding: 16, position: 'relative' }}>
                <pre style={{ fontSize: 11, color: '#00d55b', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.7 }}>
                  {activeTab === 'inline' ? inlineCode : popupCode}
                </pre>
                <button onClick={() => copyCode(activeTab === 'inline' ? inlineCode : popupCode)}
                  style={{ position: 'absolute', top: 10, right: 10, padding: '5px 12px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                  Copy
                </button>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Live Preview ── */}
          <div style={{ position: 'sticky', top: 24, alignSelf: 'start' }}>
            <div style={{ ...box, background: '#0d0d0d' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>👁️ Live Preview</h3>
              <div style={{ background: isDark ? '#0a0a0a' : '#f0f0f0', borderRadius: 12, padding: 20, display: 'flex', justifyContent: 'center' }}>
                <div style={{
                  background: prevBg, borderRadius: 16, padding: '28px 24px',
                  width: '100%', maxWidth: Math.min(parseInt(width), 420) + 'px',
                  boxShadow: isDark ? '0 4px 30px rgba(0,0,0,0.4)' : '0 4px 30px rgba(0,0,0,0.08)',
                }}>
                  <div style={{ fontSize: Math.round(parseInt(fontSize) * 1.57), fontWeight: 800, color: prevText, marginBottom: 4, fontFamily: 'sans-serif' }}>{title || 'Contact Us'}</div>
                  <div style={{ fontSize: Math.round(parseInt(fontSize) * 0.93), color: prevSub, marginBottom: 20, fontFamily: 'sans-serif' }}>{subtitle || "We'll get back to you as soon as possible."}</div>

                  {allPreviewFields.map((f, i) => (
                    <div key={f.id || i} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: Math.round(parseInt(fontSize) * 0.86), fontWeight: 700, color: '#888', marginBottom: 4, fontFamily: 'sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {f.label || `Field ${i + 1}`}{f.required ? ' *' : ''}
                      </div>
                      <div style={{
                        width: '100%',
                        height: (f.type === 'textarea' || f.label === 'Message' || f.label === 'Concern') ? 70 : 38,
                        border: `1.5px solid ${prevBord}`,
                        borderRadius: parseInt(rounded),
                        background: prevInp,
                        boxSizing: 'border-box',
                      }} />
                    </div>
                  ))}

                  <div style={{ width: '100%', padding: '13px', background: color, borderRadius: parseInt(rounded), color: '#000', fontWeight: 800, textAlign: 'center', fontSize: Math.round(parseInt(fontSize) * 1.07), fontFamily: 'sans-serif', marginTop: 8, cursor: 'pointer' }}>
                    {btnText || 'Send Message →'}
                  </div>
                  <div style={{ textAlign: 'center', fontSize: 11, color: '#ccc', marginTop: 12, fontFamily: 'sans-serif' }}>Powered by MSChat</div>
                </div>
              </div>
            </div>

            {/* Where leads go */}
            <div style={box}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>📊 Where leads appear</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { icon: '👥', title: 'Leads Page',  desc: 'Form submissions appear with 📝 Form badge' },
                  { icon: '📋', title: 'CRM Page',    desc: 'Manage and follow up on form leads' },
                ].map(item => (
                  <div key={item.title} style={{ background: '#0d0d0d', borderRadius: 8, padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ fontSize: 22 }}>{item.icon}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: '#555' }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </DashLayout>
  );
}